<script lang="ts" setup>
import { DateTime } from 'luxon'
import { NEW_FEATURE_CONFIRMED_KEY_PREFIX } from '~/constants'

interface Props {
  /** Unique key to track if this popup has been shown */
  featureKey: string
  /** The message to display in the popup */
  message: string
  /** Title for the popup */
  title?: string
  /** Expiration date - popup won't show after this date (YYYY-MM-DD format) */
  expirationDate: string
  /** Placement of the popover */
  placement?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'left-start'
    | 'left-end'
    | 'right-start'
    | 'right-end'
  /** Width of the popover */
  width?: number
  /** Delay in milliseconds before showing the popover */
  delay?: number
}

const props = withDefaults(defineProps<Props>(), {
  title: '✨ New!',
  placement: 'bottom',
  width: 300,
  delay: 3000, // 3 seconds default
})

const shouldShowPopover = ref(false)
const popoverVisible = ref(false)
const allowClose = ref(false)

const localStorageConfirmedKey = computed(
  () => `${NEW_FEATURE_CONFIRMED_KEY_PREFIX}${props.featureKey}`
)

// Check if we should show the popover
onMounted(() => {
  const now = DateTime.now()
  const expiration = DateTime.fromISO(props.expirationDate)
  if (now > expiration) return
  const alreadyShown = localStorage.getItem(localStorageConfirmedKey.value)
  if (alreadyShown) return
  shouldShowPopover.value = true
  // Show the popover after the specified delay
  nextTick(() => {
    setTimeout(() => {
      popoverVisible.value = true
    }, props.delay)
  })
})

function handleGotIt() {
  localStorage.setItem(localStorageConfirmedKey.value, 'true')
  allowClose.value = true
  popoverVisible.value = false
}

function onBeforeLeave() {
  // Prevent closing unless explicitly allowed
  if (!allowClose.value) {
    // Re-show the popover immediately if it's trying to close without permission
    nextTick(() => {
      popoverVisible.value = true
    })
    return false
  }
}

const POPOVER_OPTIONS = {
  modifiers: [
    {
      name: 'eventListeners',
      options: {
        scroll: false,
        resize: false,
      },
    },
  ],
}
</script>

<template>
  <el-popover
    :visible="popoverVisible"
    :width="width"
    :placement="placement"
    :show-arrow="true"
    :hide-after="0"
    :auto-close="0"
    :popper-options="POPOVER_OPTIONS"
    transition="el-zoom-in-top"
    @before-leave="onBeforeLeave"
  >
    <template #reference>
      <slot />
    </template>
    <template #default>
      <div class="p-1 new-feature-content" @click.stop>
        <div class="font-semibold mb-2 text-sm">{{ title }}</div>
        <div class="mb-3 text-xs leading-snug">{{ message }}</div>
        <div class="flex justify-end">
          <el-button
            type="primary"
            size="small"
            class="w-full"
            @click="handleGotIt"
          >
            Got it!
          </el-button>
        </div>
      </div>
    </template>
  </el-popover>
</template>
